import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MAX_ACCOUNT_SLOTS } from '@aiai/shared';
import { RotateCw } from 'lucide-react';
import { fetchAccounts } from '../api';
import { lowestFreeSlot, slotPath } from '../lib/accountSlot';
import { Spinner } from '../components/Spinner';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/**
 * Choose an account: after signing out of one, or when a tab's slot is gone.
 * Plain anchors on purpose: switching account is a full navigation, so a tab
 * never changes identity in-app.
 */
export function AccountsPage() {
  useDocumentTitle('Choose an account');
  const { data: accounts, isLoading } = useQuery({
    queryKey: ['auth', 'accounts'],
    queryFn: () => fetchAccounts(),
  });

  if (isLoading) {
    return (
      <div className="grid min-h-screen place-items-center px-4">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (!accounts || accounts.length === 0) return <Navigate to="/login" replace />;

  const free = lowestFreeSlot(accounts);
  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex flex-col items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
            <RotateCw className="h-6 w-6" aria-hidden="true" />
          </span>
          <h1 className="text-lg font-semibold tracking-tight text-ink">Choose an account</h1>
        </div>
        <div className="card space-y-3 p-6">
          <ul className="space-y-1">
            {accounts.map((a) => (
              <li key={a.slot}>
                <a
                  href={slotPath(a.slot, '/')}
                  className="block rounded-[5px] px-3 py-2 text-sm text-ink hover:bg-surface-2"
                >
                  {a.email}
                </a>
              </li>
            ))}
          </ul>
          {free !== null ? (
            <a href={slotPath(free, '/login?add=1')} className="btn-secondary btn-sm">
              Add another account
            </a>
          ) : (
            <p className="text-xs text-ink-muted">
              Signed in to the maximum of {MAX_ACCOUNT_SLOTS} accounts.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
